import Icon from '@ui/icons/icon';
import styles from './video-player.module.css';
import { ICONS } from '@ui/icons/types';
import React, { useCallback, useState, type SyntheticEvent, useEffect, type CSSProperties, useRef } from 'react';
import { formatMsToHMMSS } from '@/utils/dates';
import classNames from 'classnames';

interface Props {
  videoUrl: string;
  thumbnailUrl?: string;
  nextVideo: () => void;
  lastVideo: () => void;
  canSelectNext: boolean;
  canSelectLast: boolean;
}
export default function VideoPlayer({
  videoUrl,
  thumbnailUrl,
  nextVideo,
  lastVideo,
  canSelectLast,
  canSelectNext,
}: Props) {
  const [secondsPlayed, setSecondsPlayed] = useState(0);
  const [duration, setDuration] = useState(0);
  const [pause, setPause] = useState(true);
  const [canPlay, setCanPlay] = useState(false);
  const [mute, setMute] = useState(false);
  const [video, setVideo] = useState<HTMLVideoElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const tickRef = useRef<NodeJS.Timeout | null>(null);
  const parentRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);
  const dragData = useRef<{ left: number; x: number; width: number }>({
    left: 0,
    x: 0,
    width: 0,
  });

  const onMouseDownThumb = useCallback((e: React.PointerEvent) => {
    e.stopPropagation();
    draggingRef.current = true;
    if (trackRef.current) {
      const rect = trackRef.current.getBoundingClientRect();
      dragData.current.left = rect.left;
      dragData.current.width = rect.width;
      dragData.current.x = e.screenX;
    }
  }, []);

  const onMouseMove = useCallback((e: PointerEvent) => {
    if (draggingRef.current && trackRef.current && parentRef.current) {
      const delta = e.screenX - dragData.current.x;
      parentRef.current.style.setProperty('--thumb-progress', `${delta}px`);
    }
  }, []);

  const onMouseUp = useCallback(
    (e: PointerEvent) => {
      if (draggingRef.current && parentRef.current && video) {
        const delta = e.screenX - dragData.current.x;
        video.currentTime = video.duration * (delta / dragData.current.width);
        draggingRef.current = false;
        parentRef.current.style.setProperty('--thumb-progress', `0px`);
        setSecondsPlayed(video.currentTime);
      }
    },
    [video]
  );

  const onPlaying = useCallback(() => {
    setPlaying(true);
  }, []);

  const onEnded = useCallback(() => {
    if (video) {
      setPause(true);
      setPlaying(false);
      setSecondsPlayed(duration);
    }
  }, [video, duration]);

  const onPause = useCallback(() => {
    setPlaying(false);
  }, []);

  const onCanPlayThrough = useCallback((e: SyntheticEvent<HTMLVideoElement, Event>) => {
    if (e.target instanceof HTMLVideoElement) {
      setDuration(e.target.duration);
      setCanPlay(true);
    }
  }, []);

  const togglePlay = useCallback(() => {
    // user wants to play video but it's basically ended, start it over
    if (pause && video && video.currentTime > video.duration - 1) {
      video.currentTime = 0;
      setSecondsPlayed(0);
    }

    setPause(!pause);
  }, [pause, video]);

  const toggleMute = useCallback(() => {
    setMute((prev) => !prev);
  }, []);

  useEffect(() => {
    const checkProgress = () => {
      setSecondsPlayed(video?.currentTime || 0);
    };

    if (playing && video) {
      tickRef.current = setInterval(checkProgress, 1000);
    }

    return () => {
      if (tickRef.current) {
        clearInterval(tickRef.current);
      }
    };
  }, [playing, video]);

  useEffect(() => {
    if (pause) {
      video?.pause();
    } else {
      video?.play().catch(console.error);
    }
  }, [pause, video]);

  useEffect(() => {
    if (!video) return;

    if (mute) {
      video.volume = 0;
    } else {
      video.volume = 1;
    }
  }, [video, mute]);

  useEffect(() => {
    window.addEventListener('pointermove', onMouseMove);
    window.addEventListener('pointerup', onMouseUp);

    return () => {
      window.removeEventListener('pointermove', onMouseMove);
      window.removeEventListener('pointerup', onMouseUp);
    };
  }, [onMouseMove, onMouseUp]);

  useEffect(() => {
    setPause(true);
    setDuration(0);
    setSecondsPlayed(0);
  }, [videoUrl, video]);

  return (
    <div
      ref={parentRef}
      className={styles.videoPlayerContainer}
      style={
        {
          '--progress': (secondsPlayed / duration) * 100 + '%',
        } as CSSProperties
      }
    >
      <video
        ref={(ref) => setVideo(ref)}
        onCanPlay={onCanPlayThrough}
        onPlaying={onPlaying}
        onPause={onPause}
        onEnded={onEnded}
        className={styles.videoPlayer}
        src={videoUrl}
        poster={thumbnailUrl}
      />
      <div className={styles.videoControls}>
        <div className={styles.videoTrackContainer} ref={trackRef}>
          <div className={styles.videoTrack}>
            <div className={styles.videoPlayed} />
          </div>
          {duration >= 2 && <div className={styles.videoTrackThumb} onPointerDown={onMouseDownThumb} />}
        </div>
        <div className={styles.videoButtons}>
          <div className={styles.left}>
            <button onClick={togglePlay} className="no-outline" disabled={!canPlay}>
              {!pause ? <Icon name={ICONS.PAUSE_SHADOW} size={16} /> : <Icon name={ICONS.PLAY_SHADOW} size={16} />}
            </button>
            <button onClick={toggleMute} className={classNames('no-outline', styles.mute)}>
              {mute ? <Icon name={ICONS.MUTED_SHADOW} size={16} /> : <Icon name={ICONS.MUTE_SHADOW} size={16} />}
            </button>
            <span className={classNames('S2', styles.videoTime)}>
              <span>{formatMsToHMMSS(secondsPlayed * 1000)}</span>
              <span className="S1 margin-h-5">/</span>
              <span>{formatMsToHMMSS(duration * 1000)}</span>
            </span>
          </div>
          <div className={styles.right}>
            <button onClick={lastVideo} disabled={!canSelectLast}>
              <Icon name={ICONS.ANGLE_LEFT} size={16} />
            </button>
            <button onClick={nextVideo} disabled={!canSelectNext}>
              <Icon name={ICONS.ANGLE_RIGHT} size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
